import { Tournament, TournamentFollowWithDetails, TournamentStatus, TournamentType } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { AdBanner, FiltersModal, PatternBackground, TournamentCard } from '@/src/presentation/components';
import { SortOption } from '@/src/presentation/hooks';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { createStyles } from './ExploreScreen.styles';

export type ExploreTab = 'discover' | 'following';

export interface ExploreScreenProps {
  activeTab: ExploreTab;
  onTabChange: (tab: ExploreTab) => void;
  isLoggedIn: boolean;
  filteredTournaments: Tournament[];
  followedTournaments: TournamentFollowWithDetails[];
  loading: boolean;
  loadingMore: boolean;
  followingLoading: boolean;
  followingLoadingMore: boolean;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filterStatus: TournamentStatus | 'all';
  setFilterStatus: (value: TournamentStatus | 'all') => void;
  filterType: TournamentType | 'all';
  setFilterType: (value: TournamentType | 'all') => void;
  sortBy: SortOption;
  setSortBy: (value: SortOption) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  filtersVisible: boolean;
  setFiltersVisible: (value: boolean) => void;
  handleTournamentPress: (tournamentId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    primary: string;
    primaryText: string;
    error: string;
    overlay: string;
    divider: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
  };
}

export const ExploreScreen = ({
  activeTab,
  onTabChange,
  isLoggedIn,
  filteredTournaments,
  followedTournaments,
  loading,
  loadingMore,
  followingLoading,
  followingLoadingMore,
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  filterType,
  setFilterType,
  sortBy,
  setSortBy,
  hasActiveFilters,
  clearFilters,
  filtersVisible,
  setFiltersVisible,
  handleTournamentPress,
  onRefresh,
  onLoadMore,
  colors,
}: ExploreScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  const renderTournamentItem = ({ item }: { item: Tournament }) => (
    <TournamentCard tournament={item} onPress={() => handleTournamentPress(item.id)} colors={colors} />
  );

  const renderFollowedTournamentItem = ({ item }: { item: TournamentFollowWithDetails }) => {
    // Convert TournamentFollowWithDetails to Tournament-like object for TournamentCard
    const tournament: Tournament = {
      id: item.tournamentId,
      name: item.tournamentName || 'Unknown Tournament',
      description: item.tournamentDescription,
      status: (item.tournamentStatus as Tournament['status']) || 'setup',
      tournamentType: (item.tournamentType as Tournament['tournamentType']) || 'single_elimination',
      isPublic: item.tournamentIsPublic ?? true,
      maxParticipants: item.tournamentMaxParticipants,
      startDate: item.tournamentStartDate,
      location: item.tournamentLocation,
      createdAt: item.createdAt,
      updatedAt: item.createdAt,
      userId: '',
    };

    return (
      <TournamentCard
        tournament={tournament}
        onPress={() => handleTournamentPress(item.tournamentId)}
        colors={colors}
        followerCount={item.followerCount}
      />
    );
  };

  const isCurrentTabLoading = activeTab === 'discover' ? loading : followingLoading;
  const isCurrentTabLoadingMore = activeTab === 'discover' ? loadingMore : followingLoadingMore;

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        {/* Tabs */}
        <View style={[styles.tabsContainer, { borderBottomColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.tab,
              {
                borderBottomColor: activeTab === 'discover' ? colors.primary : 'transparent',
              },
            ]}
            onPress={() => onTabChange('discover')}
          >
            <Text
              style={[
                styles.tabText,
                { color: activeTab === 'discover' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('explore.discover')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.tab,
              {
                borderBottomColor: activeTab === 'following' ? colors.primary : 'transparent',
              },
            ]}
            onPress={() => onTabChange('following')}
          >
            <Text
              style={[
                styles.tabText,
                { color: activeTab === 'following' ? colors.primary : colors.textSecondary },
              ]}
            >
              {t('navigation.following')}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Search Bar with filter button - Only for Discover tab */}
        {activeTab === 'discover' && (
          <>
            <View style={styles.searchRow}>
              <View style={[styles.searchContainer, { backgroundColor: colors.background, borderColor: colors.border }]}>
                <Ionicons name="search" size={20} color={colors.textTertiary} style={{ marginRight: 8 }} />
                <TextInput
                  style={[styles.searchInput, { color: colors.text }]}
                  placeholder={t('explore.searchPublicTournaments')}
                  placeholderTextColor={colors.textTertiary}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                />
                {searchQuery.length > 0 && (
                  <TouchableOpacity onPress={() => setSearchQuery('')}>
                    <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                  </TouchableOpacity>
                )}
              </View>

              <TouchableOpacity
                style={[
                  styles.filterIconBtn,
                  {
                    backgroundColor: hasActiveFilters ? colors.primary : colors.card,
                    borderColor: colors.border,
                  },
                ]}
                onPress={() => setFiltersVisible(true)}
              >
                <Ionicons name="filter" size={22} color={hasActiveFilters ? colors.primaryText : colors.text} />
                {hasActiveFilters && (
                  <View style={[styles.filterBadge, { backgroundColor: colors.error }]}>
                    <Text style={styles.filterBadgeText}>
                      {[filterStatus !== 'all', filterType !== 'all', sortBy !== 'date-desc'].filter(Boolean).length}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {hasActiveFilters && (
              <TouchableOpacity style={[styles.clearAllFiltersBtn, { borderColor: colors.border }]} onPress={clearFilters}>
                <Text style={[styles.clearAllFiltersBtnText, { color: colors.textSecondary }]}>{t('common.clearFilters')}</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>

      {activeTab === 'discover' ? (
        <FlatList
          data={filteredTournaments}
          keyExtractor={(item) => item.id}
          renderItem={renderTournamentItem}
          refreshing={loading}
          onRefresh={onRefresh}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                {hasActiveFilters ? t('common.noResultsFilters') : t('explore.noPublicTournaments')}
              </Text>
              <Text style={[styles.emptySub, { color: colors.textTertiary }]}>
                {hasActiveFilters ? t('common.tryAdjustingFilters') : t('explore.beFirstToCreate')}
              </Text>
            </View>
          }
          ListFooterComponent={
            <>
              {loadingMore && (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <Text style={[{ color: colors.textSecondary, fontSize: 14 }]}>{t('common.loading')}</Text>
                </View>
              )}
              {filteredTournaments.length > 0 && <AdBanner colors={colors} />}
            </>
          }
          contentContainerStyle={filteredTournaments.length === 0 && styles.emptyList}
        />
      ) : (
        <FlatList
          data={followedTournaments}
          keyExtractor={(item) => item.id}
          renderItem={renderFollowedTournamentItem}
          refreshing={followingLoading}
          onRefresh={onRefresh}
          onEndReached={onLoadMore}
          onEndReachedThreshold={0.5}
          ListEmptyComponent={
            <View style={styles.empty}>
              {!isLoggedIn ? (
                <>
                  <Ionicons name="log-in-outline" size={48} color={colors.textTertiary} style={{ marginBottom: 16 }} />
                  <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('explore.loginToSeeFollowed')}</Text>
                  <Text style={[styles.emptySub, { color: colors.textTertiary }]}>
                    {t('explore.signInToFollow')}
                  </Text>
                </>
              ) : (
                <>
                  <Ionicons name="heart-outline" size={48} color={colors.textTertiary} style={{ marginBottom: 16 }} />
                  <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('explore.noFollowedTournaments')}</Text>
                  <Text style={[styles.emptySub, { color: colors.textTertiary }]}>
                    {t('explore.exploreAndFollow')}
                  </Text>
                </>
              )}
            </View>
          }
          ListFooterComponent={
            <>
              {followingLoadingMore && (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <Text style={[{ color: colors.textSecondary, fontSize: 14 }]}>{t('common.loading')}</Text>
                </View>
              )}
              {followedTournaments.length > 0 && <AdBanner colors={colors} />}
            </>
          }
          contentContainerStyle={followedTournaments.length === 0 && styles.emptyList}
        />
      )}

      {/* Filters Modal */}
      <FiltersModal
        visible={filtersVisible}
        onClose={() => setFiltersVisible(false)}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterType={filterType}
        setFilterType={setFilterType}
        sortBy={sortBy}
        setSortBy={setSortBy}
        colors={colors}
      />
    </PatternBackground>
  );
};
